import { useMemo } from "react";
import { ScheduleCard } from "./types";

interface UseScheduleCardsWithDefaultsParams {
  cards: ScheduleCard[];
  viewStart: Date | undefined;
  viewEnd: Date | undefined;
  defaultWarehouseSize: string;
  enabled: boolean;
}

const FOUR_WEEKS_MS = 4 * 7 * 24 * 60 * 60 * 1000;

export function useScheduleCardsWithDefaults({
  cards,
  viewStart,
  viewEnd,
  defaultWarehouseSize,
  enabled,
}: UseScheduleCardsWithDefaultsParams): ScheduleCard[] {
  return useMemo(() => {
    if (!enabled || !viewStart || !viewEnd) return cards;

    const now = new Date();
    const startOfCurrentHour = new Date(now);
    startOfCurrentHour.setMinutes(0, 0, 0);

    const effectiveStart = viewStart > startOfCurrentHour ? viewStart : startOfCurrentHour;
    const fourWeeksCap = new Date(now.getTime() + FOUR_WEEKS_MS);
    const effectiveEnd = viewEnd < fourWeeksCap ? viewEnd : fourWeeksCap;

    if (effectiveStart >= effectiveEnd) return cards;

    // Collect empty hourly slots
    const emptySlots: { start: Date; end: Date }[] = [];
    const slotStart = new Date(effectiveStart);

    while (slotStart < effectiveEnd) {
      const slotEnd = new Date(slotStart);
      slotEnd.setHours(slotEnd.getHours() + 1);

      const hasOverlap = cards.some(card => {
        const cardStart = new Date(card.start);
        const cardEnd = new Date(card.end);
        return cardStart < slotEnd && cardEnd > slotStart;
      });

      if (!hasOverlap) {
        emptySlots.push({ start: new Date(slotStart), end: new Date(slotEnd) });
      }

      slotStart.setHours(slotStart.getHours() + 1);
    }

    // Merge consecutive slots into continuous blocks
    const defaultCards: ScheduleCard[] = [];
    let i = 0;
    while (i < emptySlots.length) {
      const blockStart = emptySlots[i].start;
      let blockEnd = emptySlots[i].end;

      // Extend while next slot is consecutive
      while (i + 1 < emptySlots.length && emptySlots[i + 1].start.getTime() === blockEnd.getTime()) {
        i++;
        blockEnd = emptySlots[i].end;
      }

      defaultCards.push({
        id: `default-${blockStart.toISOString()}`,
        title: "",
        start: blockStart.toISOString(),
        end: blockEnd.toISOString(),
        type: "default",
        status: "approved",
        change: "no_change",
        sizeTo: defaultWarehouseSize,
        sizeFrom: defaultWarehouseSize,
      });

      i++;
    }

    return [...cards, ...defaultCards];
  }, [cards, viewStart, viewEnd, defaultWarehouseSize, enabled]);
}
